import React, { useState, useCallback, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button, Spinner } from "reactstrap";
import { Form } from "@unform/web";
import queryString from "query-string";
import * as Yup from "yup";

import CustomInput from "../../../components/Input";
import PageHero from "../../../components/PageHero";
import ShellCard from "../../../components/ShellCard";
import CardResult from "./components/CardResult";
import "./Tracking.css";
import api from "../../../services/api";
import { formatDate } from "../../../utils/formatDate";
import getValidationErrors from "../../../utils/getValidationErrors";
import { useToast } from "../../../hooks/toast";
import { useAuth } from "../../../hooks/auth";
import { useCompanies } from "../../../hooks/companies";
import { getDeliveryStatus, rotuloCidade } from "./status";
import { lerRecentes, limparRecentes, quandoFoi, salvarRecente } from "./recentes";

const onlyDigits = (message) => Yup.string().matches(/^[0-9]*$/, message);

const cnpjRule = onlyDigits("CPF ou CNPJ deve ser digitado somente com números!").required(
  "CPF ou CNPJ do destinatário é obrigatório nesta busca!"
);

// Uma opção por tipo de busca, num controle só. Antes eram 2 cards
// (Rastreamento / Referência) e, dentro de Referência, 3 chips: pedido levava
// dois cliques e dois níveis visuais. `param` é o nome do query param de
// GET /tracking; o schema é o mesmo de antes para cada tipo.
const SEARCH_MODES = [
  {
    value: "idNf",
    label: "Rastreio Everlog",
    tag: "Rastreio",
    icon: "icon-delivery-fast",
    title: "Número de rastreamento Everlog",
    // É o id interno da nota no Hub, não o código da transportadora (o Hub não
    // guarda o código do carrier). Sem o "Everlog", o operador cola o código
    // dos Correios e a busca nunca acha.
    hint: "Número gerado pelo Hub, exibido como “Rastreio Everlog” na entrega.",
    placeholder: "Ex.: 5896813",
    schema: Yup.object().shape({
      search: onlyDigits("Número de rastreio deve ser digitado somente com números!")
        .max(10, "Número de rastreio tem no máximo 10 dígitos!")
        .required("Número de rastreio é obrigatório!"),
    }),
  },
  {
    value: "chaveNf",
    label: "Chave NF-e",
    tag: "NF-e",
    icon: "icon-paper",
    title: "Chave de acesso da NF-e",
    hint: "44 dígitos, no DANFE, abaixo do código de barras.",
    placeholder: "Cole a chave de acesso (com ou sem espaços)",
    schema: Yup.object().shape({
      search: Yup.string().required("Chave de acesso da NF-e é obrigatória!"),
    }),
  },
  {
    value: "numPedido",
    label: "Pedido",
    tag: "Pedido",
    icon: "icon-bag-16",
    title: "Número do pedido",
    hint: "Somente números. Informe também o CPF ou CNPJ do destinatário.",
    placeholder: "Ex.: 000532",
    needsDocument: true,
    schema: Yup.object().shape({
      search: onlyDigits("Número do pedido deve ser digitado somente com números!").required(
        "Número do pedido é obrigatório!"
      ),
      cnpj: cnpjRule,
    }),
  },
  {
    value: "cepEntrega",
    label: "CEP de entrega",
    tag: "CEP",
    icon: "icon-square-pin",
    title: "CEP de entrega",
    hint: "8 dígitos, somente números. Informe também o CPF ou CNPJ do destinatário.",
    placeholder: "Ex.: 79045240",
    needsDocument: true,
    schema: Yup.object().shape({
      // PO1-2887: o backend EXIGE 8 dígitos (`^\d{8}$`). Um CEP mais curto
      // voltava 400 e o usuário via "Erro de conexão com o servidor!".
      search: onlyDigits("Para CEP deve ser digitado somente com números!")
        .length(8, "CEP deve ter 8 dígitos!")
        .required("CEP obrigatório!"),
      cnpj: cnpjRule,
    }),
  },
];

const findMode = (value) => SEARCH_MODES.find((m) => m.value === value) || SEARCH_MODES[0];

// A chave costuma ser colada do DANFE com espaços; o backend espera só dígitos.
const normalizeSearch = (mode, value) =>
  mode === "chaveNf" ? String(value || "").replace(/\s+/g, "") : value;

const mapNotas = (data) =>
  data.map((nf) => ({
    dt_emissao: nf.data_emissao ? formatDate(nf.data_emissao) : null,
    dt_previsao_entrega: nf.previsao_entrega ? formatDate(nf.previsao_entrega) : null,
    dt_entrega: nf.entrega ? formatDate(nf.entrega) : null,
    ...nf,
  }));

const TONE_CLASS = {
  success: "success",
  warning: "warning",
  danger: "danger",
  info: "info",
  neutral: "neutral",
};

const BuscasRecentes = ({ itens, onOpen, onClear }) => (
  <ShellCard
    className="tracking-recent"
    title="Suas buscas recentes"
    description="O status é o da última consulta. Clique para consultar de novo."
    action={
      <button type="button" className="tracking-mini-button" onClick={onClear}>
        Limpar
      </button>
    }
  >
    <ul className="tracking-recent__list">
      {itens.map((item) => (
        <li key={`${item.tipo}-${item.valor}-${item.cnpj || ""}`}>
          <button type="button" onClick={() => onOpen(item)}>
            <span className="tracking-recent__tag">{findMode(item.tipo).tag}</span>
            <span className="tracking-recent__id">
              {item.nf ? `NF ${item.nf}` : item.valor}
              {item.rota ? <small>{item.rota}</small> : null}
            </span>
            {item.status ? (
              <span
                className={`tracking-status-badge tracking-status-badge--${
                  TONE_CLASS[item.tone] || "neutral"
                }`}
              >
                {item.status}
              </span>
            ) : (
              <span />
            )}
            <span className="tracking-recent__when">{quandoFoi(item.em)}</span>
          </button>
        </li>
      ))}
    </ul>
  </ShellCard>
);

const Tracking = ({ location }) => {
  const [urlParams] = useState(location.search);
  const [dataNf, setDataNf] = useState(null);
  const [mode, setMode] = useState("idNf");
  const [loading, setLoading] = useState(false);

  const formRef = useRef(null);
  const { addToast } = useToast();
  const { user } = useAuth();
  const { empresa } = useCompanies();

  const userId = user && user.id;
  const empresaId = empresa && empresa.value;
  const [recentes, setRecentes] = useState(() => lerRecentes(userId, empresaId));

  useEffect(() => {
    setRecentes(lerRecentes(userId, empresaId));
  }, [userId, empresaId]);

  const activeMode = findMode(mode);

  const changeMode = useCallback((value) => {
    setMode(value);
    setDataNf(null);
    if (formRef.current) formRef.current.setErrors({});
  }, []);

  const showError = useCallback(
    (error) => {
      setLoading(false);

      if (error instanceof Yup.ValidationError) {
        formRef.current.setErrors(getValidationErrors(error));
        return;
      }

      setDataNf(null);
      addToast({
        place: "tr",
        type: "danger",
        title: error ? "Falha na consulta" : "Não encontrada",
        description: error
          ? "Não foi possível consultar agora. Tente de novo em instantes."
          : "Nenhuma entrega encontrada com esses dados.",
        icon: "tim-icons icon-alert-circle-exc",
      });
    },
    [addToast]
  );

  const runSearch = useCallback(
    async (searchMode, data) => {
      const config = findMode(searchMode);
      setLoading(true);
      formRef.current.setErrors({});

      try {
        const values = { ...data, search: normalizeSearch(searchMode, data.search) };
        await config.schema.validate(values, { abortEarly: false });

        const result = await api.get("/tracking", {
          params: {
            [searchMode]: values.search,
            cnpj: config.needsDocument && values.cnpj ? values.cnpj : null,
          },
        });

        if (result.status !== 200 || !result.data || result.data.length === 0) {
          showError();
          return;
        }

        const notas = mapNotas(result.data);
        setDataNf(notas);
        setLoading(false);

        const primeira = notas[0];
        const status = notas.length === 1 ? getDeliveryStatus(primeira) : null;
        const origem = rotuloCidade(primeira.cidade_origem, primeira.uf_origem);
        const destino = rotuloCidade(primeira.cidade_entrega, primeira.uf_entrega);
        setRecentes(
          salvarRecente(userId, empresaId, {
            tipo: searchMode,
            valor: values.search,
            cnpj: config.needsDocument ? values.cnpj || null : null,
            nf: notas.length === 1 ? primeira.numero : null,
            rota:
              notas.length > 1
                ? `${notas.length} entregas`
                : [origem && destino ? `${origem} → ${destino}` : null, primeira.transportadora]
                    .filter(Boolean)
                    .join(" · "),
            status: status && status.label,
            tone: status && status.tone,
          })
        );
      } catch (error) {
        showError(error);
      }
    },
    [empresaId, showError, userId]
  );

  const handleSubmit = useCallback((data) => runSearch(mode, data), [mode, runSearch]);

  const openRecent = useCallback(
    (item) => {
      setMode(item.tipo);
      // O campo de CNPJ só monta depois do render do novo modo; espera um tick.
      setTimeout(() => {
        if (!formRef.current) return;
        formRef.current.setFieldValue("search", item.valor);
        if (item.cnpj) formRef.current.setFieldValue("cnpj", item.cnpj);
        runSearch(item.tipo, { search: item.valor, cnpj: item.cnpj || undefined });
      }, 0);
    },
    [runSearch]
  );

  const clearRecent = useCallback(() => {
    setRecentes(limparRecentes(userId, empresaId));
  }, [empresaId, userId]);

  // Deep link vindo de outras telas (?chaveNf= ou ?idNf=).
  useEffect(() => {
    const { chaveNf, idNf } = queryString.parse(urlParams);
    if (!chaveNf && !idNf) return;

    const linkMode = chaveNf ? "chaveNf" : "idNf";
    const value = chaveNf || idNf;
    setMode(linkMode);
    setTimeout(() => {
      if (!formRef.current) return;
      formRef.current.setFieldValue("search", value);
      runSearch(linkMode, { search: value });
    }, 0);
    // Só na montagem: o link é lido uma vez.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParams]);

  return (
    <div className="content tracking-page">
      <PageHero
        eyebrow="Operação"
        title="Rastreamento de entregas"
        description="Posição da remessa, previsão de entrega e histórico de ocorrências."
        actions={
          <Link to="/app/trackfy/operacao/entregas-pendentes" className="tracking-hero-link">
            Ver entregas pendentes
          </Link>
        }
      />

      <ShellCard className="tracking-search" bodyStyle={{ paddingTop: "1.25rem" }}>
        <div className="tracking-segmented" role="group" aria-label="Buscar por">
          {SEARCH_MODES.map((item) => {
            const isActive = item.value === mode;
            return (
              <button
                key={item.value}
                type="button"
                className={`tracking-segmented__option${isActive ? " is-active" : ""}`}
                aria-pressed={isActive}
                onClick={() => changeMode(item.value)}
              >
                <i className={`tim-icons ${item.icon}`} aria-hidden="true" />
                {item.label}
              </button>
            );
          })}
        </div>

        <Form ref={formRef} onSubmit={handleSubmit}>
          <div className={`tracking-form${activeMode.needsDocument ? " has-document" : ""}`}>
            <div className="tracking-field">
              <label htmlFor="tracking-search">{activeMode.title}</label>
              <div className="tracking-field__control">
                <i className="tim-icons icon-zoom-split" aria-hidden="true" />
                <CustomInput
                  key={mode}
                  id="tracking-search"
                  name="search"
                  type="text"
                  autoComplete="off"
                  className="tracking-input"
                  placeholder={activeMode.placeholder}
                />
              </div>
              <span className="tracking-field__hint">{activeMode.hint}</span>
            </div>

            {activeMode.needsDocument ? (
              <div className="tracking-field">
                <label htmlFor="tracking-cnpj">CPF ou CNPJ do destinatário</label>
                <div className="tracking-field__control tracking-field__control--plain">
                  <CustomInput
                    id="tracking-cnpj"
                    name="cnpj"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    className="tracking-input"
                    placeholder="Somente números"
                  />
                </div>
                <span className="tracking-field__hint">Confirma que a entrega é deste destinatário.</span>
              </div>
            ) : null}

            <div className="tracking-form__action">
              <Button className="tracking-primary-button" type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <Spinner size="sm" color="light" /> Consultando
                  </>
                ) : (
                  "Buscar entrega"
                )}
              </Button>
            </div>
          </div>
        </Form>
      </ShellCard>

      {loading ? (
        <ShellCard className="tracking-loading">
          <div className="tracking-loading-state">
            <Spinner color="info" />
            <div>
              <strong>Consultando entrega</strong>
              <span>Buscando dados de rastreamento e ocorrências.</span>
            </div>
          </div>
        </ShellCard>
      ) : dataNf ? (
        <CardResult data={dataNf} />
      ) : recentes.length > 0 ? (
        <BuscasRecentes itens={recentes} onOpen={openRecent} onClear={clearRecent} />
      ) : (
        <ShellCard className="tracking-help">
          <div className="tracking-help__body">
            <i className="tim-icons icon-bulb-63" aria-hidden="true" />
            <div>
              <strong>Não tem o número de rastreio?</strong>
              <span>
                Busque pela chave da NF-e (44 dígitos, no DANFE) ou pelo pedido com o
                CPF ou CNPJ do destinatário.
              </span>
            </div>
          </div>
        </ShellCard>
      )}
    </div>
  );
};

export default Tracking;
